import React from "react";
import { ArrowUpRight, Check, Copy, ExternalLink, Loader2, Printer, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";

/* ---------------------------------------------------------------------------
   Shared primitives. Two surfaces share these components:
   · owner / marketing surfaces — editorial, generous (44–52px controls)
   · the property-manager portal (`.pm-scope`) — product-grade, compact
     (32/36px controls, 8px radii, no trailing arrows). The portal overrides
     live in index.css and key off the `pm-*` / `btn-pip` hooks set here, so
     one component serves both worlds without a prop on every call site.
--------------------------------------------------------------------------- */

/* ---------- Buttons ---------- */
const BTN_SIZE = {
  xs: "h-8 px-3 text-[12.5px] rounded-[8px] gap-1.5 pm-btn-xs",
  sm: "h-9 px-3.5 text-[13.5px] rounded-[9px] gap-1.5 pm-btn-sm",
  md: "h-11 px-5 text-[15px] rounded-btn gap-2 pm-btn-md",
  lg: "h-[52px] px-6 text-[16px] rounded-[14px] gap-2 pm-btn-lg",
};
const BTN_BASE = "group inline-flex items-center justify-center font-medium whitespace-nowrap transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out active:scale-[0.985] disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:shadow-ring";

/** Primary action. `variant`: lilla (default; ink inside the portal) | ink. `brand` → tenant colour on owner surfaces. `pip` → trailing arrow (hidden inside the portal). */
export const PrimaryButton = React.forwardRef(({ children, className, loading, pip = true, brand = false, variant = "lilla", size = "md", ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      BTN_BASE,
      BTN_SIZE[size] || BTN_SIZE.md,
      brand ? "bg-brandPrimary text-brandPrimaryContrast hover:brightness-[1.08]" : variant === "ink" ? "bg-ink text-white hover:bg-[#2A2620]" : variant === "hero" ? "pm-cta" : "bg-lavenderCta text-lavenderCtaFg hover:bg-lavenderCtaHover",
      className,
    )}
    {...props}
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    <span className="inline-flex items-center gap-1.5">{children}</span>
    {pip && !loading && <ArrowUpRight className={cn("btn-pip shrink-0 transition-transform duration-300 ease-editorial group-hover:translate-x-0.5 group-hover:-translate-y-0.5", size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4")} strokeWidth={2.2} />}
  </button>
));
PrimaryButton.displayName = "PrimaryButton";

/** Secondary — white surface, hairline border. */
export const SecondaryButton = React.forwardRef(({ children, className, loading, size = "md", ...props }, ref) => (
  <button
    ref={ref}
    className={cn(BTN_BASE, BTN_SIZE[size] || BTN_SIZE.md, "bg-surface text-ink border border-hairline hover:bg-fill hover:border-ink/15", className)}
    {...props}
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </button>
));
SecondaryButton.displayName = "SecondaryButton";

export const GhostButton = ({ children, className, ...props }) => (
  <button type="button" className={cn("inline-flex items-center justify-center gap-1.5 rounded-[8px] px-2.5 h-8 text-[13px] font-medium text-quiet hover:bg-ink/[0.05] hover:text-ink transition-colors duration-150 ease-out disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:shadow-ring", className)} {...props}>
    {children}
  </button>
);

export const DangerButton = ({ children, className, ...props }) => (
  <button type="button" className={cn("inline-flex items-center justify-center gap-2 rounded-btn bg-surface text-[#B42318] border border-[rgba(180,35,24,0.22)] h-9 px-3.5 text-[13.5px] font-medium hover:bg-[#FCEBE9] transition-colors duration-150 disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-ring", className)} {...props}>
    {children}
  </button>
);

/* ---------- Typography ---------- */
export const Eyebrow = ({ children, className, ...props }) => (
  <p className={cn("text-[12px] font-medium tracking-[0.02em] text-ink/55", className)} {...props}>
    {children}
  </p>
);

export const H1 = ({ children, className, ...props }) => (
  <h1 className={cn("font-display text-[38px] sm:text-[48px] lg:text-[60px] text-ink", className)} {...props}>
    {children}
  </h1>
);
export const H2 = ({ children, className, ...props }) => (
  <h2 className={cn("font-display text-[30px] sm:text-[38px] text-ink", className)} {...props}>
    {children}
  </h2>
);
export const H3 = ({ children, className, ...props }) => (
  <h3 className={cn("font-heading font-bold text-[19px] sm:text-[22px] text-ink", className)} {...props}>
    {children}
  </h3>
);
export const Lead = ({ children, className, ...props }) => (
  <p className={cn("text-[15.5px] sm:text-[17px] text-ink/65 leading-[1.6]", className)} {...props}>
    {children}
  </p>
);

/* ---------- Surfaces ---------- */
export const Card = ({ children, className, ...props }) => (
  <div className={cn("rounded-card bg-surface border border-hairline shadow-soft", className)} {...props}>
    {children}
  </div>
);
export const Panel = ({ children, className, ...props }) => (
  <div className={cn("rounded-panel bg-surface border border-hairline shadow-soft", className)} {...props}>
    {children}
  </div>
);
export const InkCard = ({ children, className, ...props }) => (
  <div className={cn("rounded-card bg-ink text-white shadow-float", className)} {...props}>
    {children}
  </div>
);

/** Header row of a panel: 14px semibold title · optional one-liner · actions right · bottom hairline. */
export const PanelHeader = ({ title, sub, actions, className, testId, as: Tag = "h2" }) => (
  <div className={cn("flex items-center justify-between gap-3 px-4 sm:px-5 h-12 border-b border-[color:var(--hairline-2)]", className)} data-testid={testId}>
    <div className="min-w-0 flex items-baseline gap-2">
      <Tag className="text-[14px] font-semibold tracking-[-0.01em] text-ink truncate">{title}</Tag>
      {sub && <span className="text-[12.5px] text-quiet truncate hidden sm:inline">{sub}</span>}
    </div>
    {actions && <div className="flex items-center gap-1.5 shrink-0">{actions}</div>}
  </div>
);

export const DotGrid = ({ className }) => (
  <div aria-hidden className={cn("absolute inset-0 pointer-events-none dot-grid dot-grid-masked opacity-50", className)} />
);
export const Glow = ({ className }) => (
  <div aria-hidden className={cn("absolute -top-40 right-0 w-[600px] h-[600px] rounded-full pointer-events-none lavender-glow", className)} />
);

/* ---------- Inputs ---------- */
const inputBase =
  "w-full rounded-input bg-surface border border-ink/[0.12] px-3.5 text-[14px] text-ink placeholder:text-ink/40 transition-[border-color,box-shadow,background-color] duration-150 ease-out hover:border-ink/25 focus:outline-none focus:border-[var(--focus-border)] focus:shadow-ring disabled:opacity-60 disabled:bg-fill/60 aria-[invalid=true]:border-[#B42318]/60 aria-[invalid=true]:focus:shadow-[0_0_0_3px_rgba(180,35,24,0.14)]";

/** `icon` → leading lucide icon; `suffix` → trailing node (unit, kbd, button). */
export const FlowInput = React.forwardRef(({ className, icon: Icon, suffix, wrapperClassName, ...props }, ref) => {
  if (!Icon && !suffix) return <input ref={ref} className={cn(inputBase, "h-11 pm-ctl", className)} {...props} />;
  return (
    <span className={cn("relative block", wrapperClassName)}>
      {Icon && <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-quiet" strokeWidth={1.7} />}
      <input ref={ref} className={cn(inputBase, "h-11 pm-ctl", Icon && "!pl-9", suffix && "!pr-11", className)} {...props} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-[12px] text-quiet">{suffix}</span>}
    </span>
  );
});
FlowInput.displayName = "FlowInput";

export const FlowTextarea = React.forwardRef(({ className, ...props }, ref) => <textarea ref={ref} className={cn(inputBase, "min-h-[110px] py-2.5 leading-relaxed pm-ctl-area", className)} {...props} />);
FlowTextarea.displayName = "FlowTextarea";

/* `FlowSelect` keeps the familiar <select><option/></select> call-site API but renders the Radix select. */
const NONE = "__none__";
export const FlowSelect = React.forwardRef(({ className, children, value, onChange, disabled, "data-testid": testId, ...props }, ref) => {
  const options = React.Children.toArray(children)
    .filter((c) => c && c.props)
    .map((c) => ({ value: c.props.value == null || c.props.value === "" ? NONE : String(c.props.value), label: c.props.children, disabled: c.props.disabled }));
  const small = /\bh-(8|9|10)\b/.test(className || "");
  const current = value == null || value === "" ? NONE : String(value);
  return (
    <span ref={ref} className={cn("block min-w-0", className)} {...(props.name ? { "data-name": props.name } : {})}>
      <SelectField value={current} onChange={(v) => onChange && onChange({ target: { value: v === NONE ? "" : v } })} options={options} disabled={disabled} testId={testId} size={small ? "sm" : "md"} className="w-full" />
    </span>
  );
});
FlowSelect.displayName = "FlowSelect";

/**
 * Label + control + help/error. Defaults to a <label> so clicking the caption focuses a plain input.
 * Pass `as="div"` for *composite* controls (address picker, stepper, phone with country menu, tile
 * groups): a <label> would forward clicks to its first button — and, worse, to whichever button
 * appears after a re-render (picking an address suggestion used to re-trigger "Change" instantly).
 */
export const Field = ({ label, help, children, className, htmlFor, required, error, as: Tag = "label", labelId }) => (
  <Tag className={cn("block min-w-0", className)} {...(Tag === "label" ? { htmlFor } : {})}>
    {label && (
      <span className="flex items-baseline justify-between gap-2 mb-1.5">
        <span id={labelId} className="block text-[12.5px] font-medium text-inkSoft">
          {label}
          {required && <span className="text-[var(--pm-accent)]"> *</span>}
        </span>
      </span>
    )}
    {children}
    {error ? <span className="block text-[12px] text-[#B42318] mt-1.5" role="alert">{error}</span> : help ? <span className="block text-[12px] text-quiet mt-1.5 leading-[1.45]">{help}</span> : null}
  </Tag>
);

/** Switch with an optional label + helper. The whole row is the hit target. */
export const Toggle = ({ checked, onChange, label, help, testId, disabled, className }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled} data-testid={testId} onClick={() => onChange(!checked)} className={cn("group inline-flex items-start gap-2.5 text-left text-[13.5px] text-inkSoft disabled:opacity-50 focus-visible:outline-none", className)}>
    <span className={cn("relative mt-px inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 group-focus-visible:shadow-ring", checked ? "bg-ink" : "bg-ink/[0.16] group-hover:bg-ink/[0.24]")}>
      <span className={cn("absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-editorial", checked && "translate-x-4")} />
    </span>
    {(label || help) && (
      <span className="min-w-0">
        {label && <span className="block leading-5">{label}</span>}
        {help && <span className="block text-[12px] text-quiet mt-0.5">{help}</span>}
      </span>
    )}
  </button>
);

/** Segmented control — view toggles and small radio groups. */
export const Segmented = ({ value, onChange, options, size = "md", className, testId }) => (
  <div role="tablist" className={cn("inline-flex items-center rounded-[8px] bg-fill p-0.5 gap-0.5", className)} data-testid={testId}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <button key={o.value} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.value)} disabled={o.disabled} className={cn("inline-flex items-center justify-center gap-1.5 rounded-[6px] font-medium whitespace-nowrap transition-[background-color,color,box-shadow] duration-150 ease-out disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-ring", size === "sm" ? "h-7 px-2.5 text-[12.5px]" : "h-8 px-3 text-[13px]", on ? "bg-surface text-ink shadow-[0_1px_2px_rgba(10,10,10,0.08),0_0_0_1px_rgba(20,18,15,0.05)]" : "text-quiet hover:text-ink")} data-testid={o.testId}>
          {o.icon && <o.icon className="h-3.5 w-3.5" strokeWidth={1.7} />}
          {o.label}
          {o.count != null && <span className={cn("ml-0.5 rounded-[4px] px-1 text-[11px] tabular-nums", on ? "bg-fill text-ink" : "bg-ink/[0.06] text-quiet")}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

/** Underline tabs: ink text + 2px ink underline for the active tab; the list has a hairline. Counts read as quiet numbers. */
export const UnderlineTabs = ({ value, onChange, options, className, testId }) => (
  <div role="tablist" className={cn("flex items-center gap-6 border-b border-[color:var(--hairline)] min-w-max", className)} data-testid={testId}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <button key={o.value} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.value)} disabled={o.disabled} className={cn("relative -mb-px inline-flex items-center gap-1.5 h-10 text-[14px] font-medium whitespace-nowrap transition-colors duration-150 disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-ring rounded-t-md", on ? "text-ink" : "text-quiet hover:text-ink")} data-testid={o.testId}>
          {o.icon && <o.icon className="h-4 w-4" strokeWidth={1.75} />}
          {o.label}
          {o.count != null && <span className={cn("text-[12.5px] font-normal", on ? "text-quiet" : "text-quiet2")}>{o.count}</span>}
          <span aria-hidden className={cn("absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-ink transition-opacity duration-150", on ? "opacity-100" : "opacity-0")} />
        </button>
      );
    })}
  </div>
);

/* ---------- Badges & chips ---------- */
/* Semantic tones — one visual language for every status in the portal. Never render a raw enum key. */
const TONE = {
  neutral: "bg-fill text-inkSoft",
  quiet: "bg-transparent text-quiet ring-1 ring-inset ring-[color:var(--hairline)]",
  accent: "bg-[var(--pm-accent-soft)] text-[var(--pm-accent-ink)]",
  info: "bg-[rgba(59,111,214,0.10)] text-[#1E3F8A]",
  success: "bg-[rgba(24,121,78,0.11)] text-[#0F5132]",
  warning: "bg-[rgba(194,122,44,0.13)] text-[#6B3E12]",
  danger: "bg-[rgba(180,35,24,0.10)] text-[#9A1F15]",
  ink: "bg-ink text-white",
};
const TONE_DOT = { neutral: "#8A8478", quiet: "#B4AEA2", accent: "#9B5BD6", info: "#3B6FD6", success: "#18794E", warning: "#C27A2C", danger: "#B42318", ink: "rgba(255,255,255,0.7)" };
export const StatusBadge = ({ tone = "neutral", children, dot = true, size = "md", className, testId, ...props }) => (
  <span data-testid={testId} data-tone={tone} className={cn("inline-flex items-center gap-1.5 rounded-[6px] font-medium whitespace-nowrap", size === "sm" ? "px-1.5 h-5 text-[11px]" : "px-2 h-[22px] text-[11.5px]", TONE[tone] || TONE.neutral, className)} {...props}>
    {dot && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: TONE_DOT[tone] || TONE_DOT.neutral }} />}
    {children}
  </span>
);
/** Tone maps for the portal's enums. */
export const OFFER_TONE = { draft: "neutral", sent: "info", opened: "accent", accepted: "success", declined: "danger", superseded: "quiet", expired: "warning" };
export const EMAIL_TONE = { sent: "success", delivered: "success", logged: "neutral", queued: "info", failed: "danger", bounced: "danger" };
export const CONTRACT_TONE = { pending: "info", signed: "success", void: "quiet" };
/** Relationship with an owner, derived from their units: customer (signed) · prospect · former · lost. */
export const REL_TONE = { customer: "success", prospect: "accent", former: "neutral", lost: "quiet" };
export const RelationshipBadge = ({ relationship, size = "sm", className, testId }) =>
  relationship ? (
    <StatusBadge size={size} tone={REL_TONE[relationship] || "neutral"} className={className} testId={testId} data-relationship={relationship}>
      {t(`rel.${relationship}`)}
    </StatusBadge>
  ) : null;

/** A card / panel heading row — 14px semibold, optional one-line helper, actions right. */
export const SectionTitle = ({ title, sub, actions, className, as: Tag = "h3", testId }) => (
  <div className={cn("flex items-start justify-between gap-3", className)} data-testid={testId}>
    <div className="min-w-0">
      <Tag className="text-[14px] font-semibold tracking-[-0.01em] text-ink leading-[1.3]">{title}</Tag>
      {sub && <p className="text-[12.5px] text-quiet mt-0.5 leading-[1.45]">{sub}</p>}
    </div>
    {actions && <div className="flex items-center gap-1.5 shrink-0 -mt-1 -mr-1">{actions}</div>}
  </div>
);

/** Portal 2027 section — a white surface on the warm page: title row · hairline · content. */
export const Section = ({ title, sub, actions, children, className, testId, as: Tag = "h3", dense = false, flush = false }) => (
  <section className={cn("min-w-0 pm-surface flex flex-col", className)} data-testid={testId}>
    <div className={cn("flex items-center justify-between gap-3 min-h-[52px] px-5 border-b border-[color:var(--hairline-2)]", dense && "min-h-[46px] px-4")}>
      <div className="min-w-0 flex items-baseline gap-2.5 flex-wrap">
        <Tag className="text-[15px] font-semibold tracking-[-0.015em] text-ink leading-[1.3]">{title}</Tag>
        {sub && <span className="text-[13px] text-quiet">{sub}</span>}
      </div>
      {actions && <div className="flex items-center gap-1 shrink-0 -my-1 -mr-2">{actions}</div>}
    </div>
    <div className={cn("flex-1 min-w-0", flush ? "" : dense ? "px-4 py-3.5" : "px-5 py-4")}>{children}</div>
  </section>
);

/** Right-rail panel — a compact white surface. */
export const RailPanel = ({ title, sub, actions, children, className, testId }) => (
  <div className={cn("pm-surface p-4", className)} data-testid={testId}>
    {(title || actions) && (
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          {title && <h3 className="text-[14px] font-semibold tracking-[-0.01em] text-ink leading-[1.3]">{title}</h3>}
          {sub && <p className="text-[12.5px] text-quiet mt-0.5 leading-[1.45]">{sub}</p>}
        </div>
        {actions && <div className="flex items-center gap-1 shrink-0 -mt-1 -mr-1">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

/** Inline notice inside a page — calm tint, icon, one or two lines. */
const BANNER = {
  info: "bg-[rgba(59,111,214,0.06)] border-[rgba(59,111,214,0.18)] text-[#1E3F8A]",
  success: "bg-[rgba(24,121,78,0.06)] border-[rgba(24,121,78,0.18)] text-[#0F5132]",
  warning: "bg-[rgba(194,122,44,0.07)] border-[rgba(194,122,44,0.22)] text-[#6B3E12]",
  danger: "bg-[rgba(180,35,24,0.06)] border-[rgba(180,35,24,0.18)] text-[#9A1F15]",
  neutral: "bg-canvasAlt border-hairline text-inkSoft",
};
export const Banner = ({ tone = "neutral", icon: Icon, title, children, action, className, testId }) => (
  <div className={cn("flex items-start gap-3 rounded-[10px] border px-3.5 py-2.5 text-[13px] leading-[1.5]", BANNER[tone] || BANNER.neutral, className)} data-testid={testId} role={tone === "danger" ? "alert" : undefined}>
    {Icon && <Icon className="h-4 w-4 shrink-0 mt-0.5 opacity-80" strokeWidth={1.8} />}
    <div className="min-w-0 flex-1">
      {title && <p className="font-medium">{title}</p>}
      {children && <div className={cn(title && "opacity-80 mt-0.5")}>{children}</div>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

/** 32px icon-only button. `tone`: default | danger */
export const IconButton = React.forwardRef(({ label, tone = "default", size = "md", className, children, ...props }, ref) => (
  <button ref={ref} type="button" aria-label={label} title={label} className={cn("inline-flex items-center justify-center rounded-[8px] transition-colors duration-150 disabled:opacity-30 disabled:pointer-events-none focus-visible:outline-none focus-visible:shadow-ring", size === "sm" ? "h-7 w-7" : "h-8 w-8", tone === "danger" ? "text-quiet hover:bg-[#FCEBE9] hover:text-[#B42318]" : "text-quiet hover:bg-fill hover:text-ink", className)} {...props}>
    {children}
  </button>
));
IconButton.displayName = "IconButton";

/** Radix select — same height and focus halo as inputs.
    `options`: [{ value, label, hint?, disabled? }] · `groups`: [{ label, options }] */
export const SelectField = ({ value, onChange, options = [], groups, placeholder, disabled, className, testId, size = "md", align = "start", renderValue }) => {
  const all = groups ? groups.flatMap((g) => g.options) : options;
  const current = all.find((o) => String(o.value) === String(value));
  return (
    <Select value={value == null ? undefined : String(value)} onValueChange={(v) => onChange && onChange(v)} disabled={disabled}>
      <SelectTrigger data-testid={testId} className={cn(inputBase, size === "sm" ? "h-9 text-[13px]" : "h-11", "pm-ctl justify-between gap-2 shadow-none data-[placeholder]:text-ink/40 [&>span]:truncate [&>svg]:opacity-60 [&>svg]:shrink-0 focus:ring-0 focus:shadow-ring", className)}>
        <SelectValue placeholder={placeholder}>{renderValue ? renderValue(current) : current?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent align={align} position="popper" sideOffset={6} className="rounded-[10px] border-hairline bg-surface shadow-[0_12px_36px_rgba(10,10,10,0.12),0_0_0_1px_rgba(20,18,15,0.05)] p-1 min-w-[var(--radix-select-trigger-width)]" data-testid={testId ? `${testId}-menu` : undefined}>
        {(groups || [{ options }]).map((g, gi) => (
          <SelectGroup key={g.label || gi}>
            {g.label && <SelectLabel className="px-2.5 pt-1.5 pb-1 text-[10.5px] uppercase tracking-[0.12em] text-quiet font-medium">{g.label}</SelectLabel>}
            {g.options.map((o) => (
              <SelectItem key={o.value} value={String(o.value)} disabled={o.disabled} data-testid={testId ? `${testId}-option-${o.value}` : undefined} className="rounded-[7px] pl-2.5 pr-8 h-8 text-[13px] text-ink cursor-pointer focus:bg-fill data-[state=checked]:font-medium [&>span:first-child]:right-2.5 [&>span:first-child]:left-auto">
                <span className="flex items-center gap-2 min-w-0">
                  {o.icon && <o.icon className="h-3.5 w-3.5 text-quiet shrink-0" strokeWidth={1.8} />}
                  {o.swatch && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: o.swatch }} />}
                  <span className="truncate">{o.label}</span>
                  {o.hint && <span className="text-[12px] text-quiet truncate">{o.hint}</span>}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
};

/** Read-only fact row inside a form grid (for values the user can't change here). */
export const InfoRow = ({ label, value, hint, action, testId }) => (
  <div className="rounded-input border border-hairline bg-canvasAlt/70 px-3 py-2 min-w-0" data-testid={testId}>
    <p className="text-[11.5px] font-medium text-quiet">{label}</p>
    <div className="mt-0.5 flex items-center justify-between gap-3">
      <p className="text-[13.5px] text-ink truncate">{value ?? "—"}</p>
      {action}
    </div>
    {hint && <p className="text-[12px] text-quiet mt-1">{hint}</p>}
  </div>
);

const STAGE_TONE = {
  new: "bg-fill text-inkSoft",
  qualified: "bg-[var(--pm-accent-soft)] text-[var(--pm-accent-ink)]",
  offer: "bg-[rgba(59,111,214,0.10)] text-[#1E3F8A]",
  signed: "bg-[rgba(24,121,78,0.11)] text-[#0F5132]",
  onboarding: "bg-[rgba(194,122,44,0.13)] text-[#6B3E12]",
  ready: "bg-ink text-white",
  live: "bg-[rgba(46,144,250,0.12)] text-[#0B2E6B]",
  lost: "bg-[rgba(180,35,24,0.10)] text-[#9A1F15]",
  archived: "bg-fill text-quiet",
  ended: "bg-[rgba(107,100,89,0.14)] text-[#4A443C]",
};
export const STAGE_RAIL = { new: "#8A8478", qualified: "#9B5BD6", offer: "#3B6FD6", signed: "#18794E", onboarding: "#C27A2C", ready: "#14120F", live: "#2E90FA", lost: "#B42318", archived: "#B4AEA2", ended: "#6B6459" };
export const StageBadge = ({ stage, className, label, dot = true }) => (
  <span data-testid="pipeline-stage-badge" data-stage={stage} className={cn("inline-flex items-center gap-1.5 rounded-[6px] px-2 h-[22px] text-[11.5px] font-medium whitespace-nowrap", STAGE_TONE[stage] || STAGE_TONE.new, className)}>
    {dot && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: stage === "ready" ? "rgba(255,255,255,0.7)" : STAGE_RAIL[stage] || STAGE_RAIL.new }} />}
    {label || t(`stage.${stage}`)}
  </span>
);

const CHIP = {
  required: "bg-ink text-white",
  included: "bg-fill text-ink",
  optional: "bg-surface text-ink ring-1 ring-inset ring-[color:var(--hairline)]",
  internal: "bg-[#FFF4E5] text-[#8A5A00]",
  recommended: "bg-[var(--pm-accent-soft)] text-[var(--pm-accent-ink)]",
};
export const Chip = ({ kind = "optional", children, className }) => (
  <span data-testid={`addon-chip-${kind}`} className={cn("inline-flex items-center rounded-[6px] px-2 h-[22px] text-[11.5px] font-medium whitespace-nowrap", CHIP[kind] || CHIP.optional, className)}>
    {children || t(`chip.${kind}`)}
  </span>
);

export const Pill = ({ children, className, ...props }) => (
  <span className={cn("inline-flex items-center rounded-[6px] bg-fill px-2 h-[22px] text-[11.5px] font-medium text-quiet whitespace-nowrap", className)} {...props}>
    {children}
  </span>
);

/** 24px initials avatar with a deterministic warm tint per name. */
const AVATAR_TINTS = ["#E7E1F5", "#DCEFEC", "#F5E6D6", "#E3EBF7", "#F1E3E3", "#E6EFDD"];
const AVATAR_INK = ["#3D2E6B", "#5B2E8C", "#6B3E12", "#1E3F8A", "#7A2E2E", "#2F5A1C"];
export const InitialsAvatar = ({ name, className, size = "md", testId }) => {
  const s = String(name || "?");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const i = h % AVATAR_TINTS.length;
  const parts = s.trim().split(/\s+/);
  const ini = (parts[0]?.[0] || "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-semibold uppercase select-none", size === "sm" ? "h-5 w-5 text-[9px]" : size === "lg" ? "h-8 w-8 text-[12px]" : "h-6 w-6 text-[10px]", className)} style={{ background: AVATAR_TINTS[i], color: AVATAR_INK[i] }} data-testid={testId} aria-hidden>
      {ini.toUpperCase()}
    </span>
  );
};

/** Keyboard key hint. */
export const Kbd = ({ children, className }) => (
  <kbd className={cn("inline-flex items-center justify-center rounded-[5px] border border-hairline bg-surface px-1.5 h-5 min-w-[20px] text-[10.5px] font-medium text-quiet", className)}>{children}</kbd>
);

/* ---------- Progress ---------- */
export const ProgressBar = ({ value = 0, className, brand = false, testId }) => (
  <div className={cn("h-1 w-full rounded-full bg-hairline overflow-hidden", className)} data-testid={testId}>
    <div className={cn("h-full rounded-full transition-[width] duration-500 ease-editorial", brand ? "bg-brandPrimary" : "bg-ink")} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
  </div>
);

export const CheckDot = ({ done, className }) => (
  <span className={cn("inline-flex h-5 w-5 items-center justify-center rounded-full border", done ? "bg-success border-success text-white" : "border-hairline bg-surface", className)}>
    {done && <Check className="h-3 w-3" strokeWidth={3} />}
  </span>
);

/* ---------- States ---------- */
export const Spinner = ({ className }) => <Loader2 className={cn("h-5 w-5 animate-spin text-quiet", className)} />;

export const Skeleton = ({ className }) => <div className={cn("shimmer rounded-lg", className)} data-testid="loading-state" />;

/* Calm empty state: one line, one action — no dashed boxes, no icon tiles. */
export const EmptyState = ({ title, sub, action, icon: Icon, className, compact = false }) => (
  <div className={cn("relative text-center pm-surface overflow-hidden", compact ? "px-4 py-8" : "px-6 py-14", className)} data-testid="empty-state">
    <span aria-hidden className="pointer-events-none absolute -top-16 left-1/2 h-40 w-72 -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(155,91,214,0.16),transparent)]" />
    {Icon && <span className="relative mx-auto mb-3.5 flex h-11 w-11 items-center justify-center rounded-[14px] bg-surface text-quiet shadow-[var(--pm-shadow-card)]"><Icon className="h-5 w-5" strokeWidth={1.6} aria-hidden /></span>}
    <p className="relative text-[15px] font-semibold tracking-[-0.01em] text-ink">{title}</p>
    {sub && <p className="relative mt-1.5 text-[13.5px] text-quiet max-w-[46ch] mx-auto leading-[1.55]">{sub}</p>}
    {action && <div className="relative mt-5 flex justify-center">{action}</div>}
  </div>
);

export const ErrorState = ({ title, sub, action }) => (
  <div className="rounded-card border border-[rgba(180,35,24,0.2)] bg-[#FCEBE9]/70 p-4" data-testid="error-state">
    <p className="font-medium text-[13.5px] text-[#B42318]">{title}</p>
    {sub && <p className="text-[13px] text-[#B42318]/80 mt-1">{sub}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const PoweredBy = ({ org, className }) =>
  org?.powered_by !== false ? (
    <p className={cn("text-[11px] text-taupe tracking-wide", className)} data-testid="powered-by">
      {t("brand.powered_by")}
    </p>
  ) : null;

/* Quiet stat: label 12px on top, value 24px tabular. Zero reads quiet; anything else reads ink. */
export const Stat = ({ label, value, sub, className, testId, tone }) => {
  const zero = value === 0 || value === "0";
  return (
    <div className={cn("min-w-0", className)} data-testid={testId} data-zero={zero ? "true" : "false"}>
      <p className="text-[12.5px] text-quiet leading-snug text-balance sm:truncate">{label}</p>
      <p className={cn("mt-2 text-[24px] md:text-[28px] leading-none tracking-[-0.025em] tabular-nums", zero ? "font-normal text-taupe" : cn("font-semibold", tone || "text-ink"))}>{value}</p>
      {sub && <p className="text-[12px] text-taupe mt-1.5 sm:truncate">{sub}</p>}
    </div>
  );
};

export const KeyValue = ({ k, v, testId }) => (
  <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-[minmax(96px,38%)_minmax(0,1fr)] sm:items-start sm:gap-3 py-2 border-b border-[color:var(--hairline-2)] last:border-0" data-testid={testId}>
    <span className="text-[12.5px] sm:text-[13px] text-quiet leading-5">{k}</span>
    <span className="text-[13px] text-ink leading-5 min-w-0 break-words">{v ?? "—"}</span>
  </div>
);

/* ---------- Facts (Portal 2026) ---------- */
/** Attribute grid for a record: label over value, 2–3 columns, no borders. `span` stretches a cell. */
export const FactGrid = ({ children, className, cols = 3, testId }) => (
  <dl className={cn("grid grid-cols-2 gap-x-6 gap-y-5", cols === 3 ? "sm:grid-cols-3" : cols === 4 ? "sm:grid-cols-4" : "", className)} data-testid={testId}>{children}</dl>
);
export const FactCell = ({ label, value, span = false, testId, className }) => (
  <div className={cn("min-w-0", span && "col-span-2 sm:col-span-full", className)} data-testid={testId}>
    <dt className="text-[12.5px] text-quiet leading-4">{label}</dt>
    <dd className="mt-1 text-[14px] text-ink leading-5 break-words">{value == null || value === "" ? <span className="text-quiet2">—</span> : value}</dd>
  </div>
);
/** Rail fact row — label over value, optional copy affordance on hover. Wrap in <dl className="divide-y …">. */
export const RailFact = ({ label, value, onCopy, action, testId }) => (
  <div className="group flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0" data-testid={testId}>
    <div className="min-w-0">
      <dt className="text-[12px] text-quiet leading-4">{label}</dt>
      <dd className="mt-0.5 text-[13.5px] text-ink leading-5 min-w-0 break-words">{value == null || value === "" ? <span className="text-quiet2">—</span> : value}</dd>
    </div>
    {action}
    {onCopy && (
      <button type="button" onClick={onCopy} aria-label={t("common.copy")} title={t("common.copy")} className="mt-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-quiet2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-fill hover:text-ink transition-[opacity,background-color,color] duration-150 focus-visible:outline-none focus-visible:shadow-ring" data-testid={testId ? `${testId}-copy` : undefined}>
        <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
    )}
  </div>
);

/* ---------- Document viewer ---------- */
/* Full-screen, read-only preview of an owner-facing document (offer, agreement) inside the portal.
   Grey canvas, paper centred, quiet top bar: title · note · actions (open as owner, copy link, print).
   Never triggers owner-side effects — callers must pass admin-safe data. */
export const DocViewer = ({ open, onOpenChange, title, note, badge, actions, ownerHref, onCopyLink, printable = true, width = "max-w-[860px]", children, testId = "doc-viewer" }) => (
  <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-ink/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content className="doc-viewer doc-editorial fixed inset-0 z-[201] flex flex-col bg-[#ECEAE4] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:inset-3 sm:rounded-[16px] sm:shadow-[0_30px_90px_rgba(10,10,10,0.35)] overflow-hidden" data-testid={testId} aria-describedby={undefined}>
        <div className="doc-viewer-bar h-14 shrink-0 flex items-center justify-between gap-3 px-3 sm:px-5 border-b border-ink/[0.08] bg-white/80 backdrop-blur-md">
          <div className="min-w-0 flex items-center gap-3">
            <DialogPrimitive.Close asChild>
              <IconButton label={t("common.close")} data-testid={`${testId}-close`}><X className="h-4 w-4" /></IconButton>
            </DialogPrimitive.Close>
            <div className="min-w-0">
              <DialogPrimitive.Title className="flex items-center gap-2 text-[14px] font-medium text-ink truncate">{title}{badge}</DialogPrimitive.Title>
              {note && <p className="text-[12px] text-quiet truncate">{note}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 print:hidden">
            {actions}
            {onCopyLink && <SecondaryButton size="sm" onClick={onCopyLink} data-testid={`${testId}-copy-link`}><Copy className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t("viewer.copy_link")}</span></SecondaryButton>}
            {ownerHref && <a href={ownerHref} target="_blank" rel="noreferrer"><SecondaryButton size="sm" data-testid={`${testId}-open-owner`}><ExternalLink className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t("viewer.open_as_owner")}</span></SecondaryButton></a>}
            {printable && <IconButton label={t("viewer.print")} onClick={() => window.print()} data-testid={`${testId}-print`}><Printer className="h-4 w-4" /></IconButton>}
          </div>
        </div>
        <div className="doc-canvas flex-1 min-h-0 overflow-y-auto pm-scroll px-3 sm:px-8 py-5 sm:py-8">
          <div className={cn("mx-auto w-full doc-paper-enter", width)}>{children}</div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);
